// Fee & Obligation PR-6 — obligation detail drawer. Shows the type's catalog
// facts joined with THIS loan's reality, the linked fee (+ PR-4a case), WS2
// anomaly flags, recent retirements, the disbursements that paid it, and the
// WS3 period comparison — with the D-27/D-28 policy stated plainly and an
// "Ask AI" button opening the shared explain engine.

import { useState, type ReactNode } from "react";
import { Sparkles } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency } from "@/design-system/lib/format";
import {
  useObligationComparison,
  useObligationDetail,
} from "@/hooks/portfolioAdmin/useObligationLedger";
import { ObligationExplainModal } from "./ObligationExplainModal";
import {
  ANOMALY_FLAG_LABEL,
  OBLIGATION_POLICY_COPY,
  PERIOD_CHANGE_LABEL,
} from "./obligationExplainCopy";

export interface ObligationDetailDrawerProps {
  loanId: string;
  obligationId: string;
  onClose: () => void;
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-space-3 border-b border-border-light py-space-1">
      <span className="text-caption text-gray-mid uppercase tracking-button shrink-0">
        {label}
      </span>
      <span className="text-small text-right">{value}</span>
    </div>
  );
}

function severityTone(s: string): "review" | "error" | "info" {
  // Badge has no "warning" tone — "review" is the amber one.
  return s === "error" ? "error" : s === "warning" ? "review" : "info";
}

export function ObligationDetailDrawer({
  loanId,
  obligationId,
  onClose,
}: ObligationDetailDrawerProps) {
  const detail = useObligationDetail(loanId, obligationId);
  const comparison = useObligationComparison(loanId);
  const [explaining, setExplaining] = useState(false);

  const d = detail.data;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Obligation detail"
        description={d ? `${d.catalog.typeDisplay} · ${d.catalog.typeCode}` : undefined}
        size="lg"
        footer={
          <div className="flex items-center justify-between gap-space-2 w-full">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setExplaining(true)}
              disabled={!d}
              data-testid="obligation-ask-ai"
            >
              <Sparkles size={14} aria-hidden /> Ask AI to explain
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        }
      >
        {detail.isLoading || !d ? (
          <p className="text-small text-gray-mid italic m-0">Loading obligation…</p>
        ) : (
          <div className="flex flex-col gap-space-4" data-testid="obligation-detail-drawer">
            {/* WS2 flags */}
            {d.flags.length > 0 && (
              <div className="flex flex-col gap-space-2" data-testid="obligation-flags">
                {d.flags.map((f) => (
                  <div
                    key={f.code}
                    className="rounded-md border border-border-light p-space-2 bg-white-warm"
                  >
                    <Badge status={severityTone(f.severity)}>
                      {ANOMALY_FLAG_LABEL[f.code] ?? f.code}
                    </Badge>
                    <p className="text-small m-0 mt-space-1">{f.explanation}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Catalog facts */}
            <section>
              <h4 className="text-small font-bold m-0 mb-space-1">What it is</h4>
              {d.catalog.description && (
                <p className="text-small text-gray-mid m-0 mb-space-2">
                  {d.catalog.description}
                </p>
              )}
              <Row label="Waterfall slot" value={
                d.catalog.currentWaterfallPriority != null
                  ? `WF${d.catalog.currentWaterfallNumber ?? "?"} · position ${d.catalog.currentWaterfallPriority}`
                  : "—"
              } />
              <Row label="Currently fires" value={d.catalog.isCurrentlyFireable ? "Yes" : "No"} />
              <Row label="Takes all remaining" value={d.catalog.isTakeAllRemaining ? "Yes" : "No"} />
              <Row label="Creation mode" value={d.catalog.creationMode ?? "—"} />
            </section>

            {/* Loan reality */}
            <section>
              <h4 className="text-small font-bold m-0 mb-space-1">This loan</h4>
              <Row label="State" value={d.reality.state} />
              <Row label="Original" value={formatCurrency(d.reality.originalAmount)} />
              <Row label="Outstanding" value={formatCurrency(d.reality.outstandingBalance)} />
              <Row label="Paid to date" value={formatCurrency(d.reality.totalPaidLifetime)} />
              {d.reality.totalCap != null && (
                <Row label="Cap" value={formatCurrency(d.reality.totalCap)} />
              )}
              <Row
                label="Recipient"
                value={d.reality.recipientName ?? d.reality.recipientNote}
              />
            </section>

            {/* Fee link + case */}
            {d.feeLink && (
              <section>
                <h4 className="text-small font-bold m-0 mb-space-1">Billing</h4>
                <Row label="Linked fee" value={`${d.feeLink.sourceType ?? "fee"} · ${d.feeLink.status}`} />
                {d.feeLink.defaultCaseId && (
                  <Row label="Workout case" value="Linked (recovery cost)" />
                )}
                {d.feeLink.docsCertified != null && (
                  <Row label="Docs certified" value={d.feeLink.docsCertified ? "Yes" : "No"} />
                )}
              </section>
            )}

            {/* Recent retirements */}
            {d.recentRetirements.length > 0 && (
              <section>
                <h4 className="text-small font-bold m-0 mb-space-1">Recent history</h4>
                {d.recentRetirements.map((r, i) => (
                  <Row
                    key={i}
                    label={r.changeType}
                    value={`${formatCurrency(r.changeAmount)} → ${formatCurrency(r.balanceAfter)}`}
                  />
                ))}
              </section>
            )}

            {/* Period comparison (WS3) */}
            {comparison.data && comparison.data.changes.length > 0 && (
              <section data-testid="obligation-comparison">
                <h4 className="text-small font-bold m-0 mb-space-1">This period vs last</h4>
                {comparison.data.changes.slice(0, 8).map((c) => (
                  <div key={c.typeCode} className="text-small py-space-1 border-b border-border-light">
                    <Badge status="info">{PERIOD_CHANGE_LABEL[c.kind] ?? c.kind}</Badge>{" "}
                    {c.explanation}
                  </div>
                ))}
              </section>
            )}

            {/* Policy (D-27 / D-28) */}
            <section className="rounded-md border border-border-light bg-white-warm p-space-2">
              <h4 className="text-small font-bold m-0 mb-space-1">How fees are handled</h4>
              <p className="text-small m-0">{OBLIGATION_POLICY_COPY.lateFeeToPhoenix}</p>
              <p className="text-small m-0 mt-space-1">{OBLIGATION_POLICY_COPY.unpaidFeesStayOpen}</p>
            </section>
          </div>
        )}
      </Modal>

      {explaining && d && (
        <ObligationExplainModal
          loanId={loanId}
          obligationId={obligationId}
          obligationLabel={`${d.catalog.typeDisplay} · ${formatCurrency(d.reality.outstandingBalance)} outstanding`}
          onClose={() => setExplaining(false)}
        />
      )}
    </>
  );
}
